/**
 * OrderRailChrome - the order strip's chrome (plan design-gaps L2): the
 * frame, the tracker's head with its controls and the custom-name box, the
 * notes under it (manual order, the join hint), the END TURN foot, the
 * empty message and the reading of the tracker's state (`orderFacts`).
 * The rows, the drags, the keys and the layout stay in OrderRail.
 */
import { useState } from 'react';
import type { ReactNode } from 'react';
import clsx from '../utils/clsx';
import styles from '../styles/OrderRail.module.css';
import type { OrderRailProps } from './OrderRail';
import type { TurnTrackerState } from '../types';

/** A custom participant's name box in the head: open, typed, submitted. */
export function useCustomDraft(onAddCustom: OrderRailProps['onAddCustom']) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    onAddCustom(trimmed);
    setName('');
    setOpen(false);
  };
  return { open, setOpen, name, setName, submit };
}

export type CustomDraft = ReturnType<typeof useCustomDraft>;

function ResetButton({
  disabled,
  locked,
  showDialog,
  onResetCombat,
}: {
  disabled: boolean;
  locked: boolean;
  showDialog: OrderRailProps['showDialog'];
  onResetCombat: OrderRailProps['onResetCombat'];
}) {
  const reset = async () => {
    if (locked) return;
    const choice = await showDialog('choice', 'Reset Combat', {
      choices: [
        { label: 'Reset Round (keep participants)', value: 'reset' },
        { label: 'New Combat (clear everything)', value: 'clear' },
      ],
    });
    if (choice === 'reset') onResetCombat(true);
    else if (choice === 'clear') onResetCombat(false);
  };
  return (
    <button
      className={styles.iconBtn}
      data-testid="turn-reset"
      onClick={reset}
      disabled={disabled}
      title="Reset combat"
    >
      ↺
    </button>
  );
}

function AddCustom({ draft, locked }: { draft: CustomDraft; locked: boolean }) {
  if (!draft.open) {
    return (
      !locked && (
        <button
          className={styles.iconBtn}
          data-testid="turn-add-custom"
          onClick={() => draft.setOpen(true)}
          title="Add custom participant"
          aria-label="Add custom participant"
        >
          +
        </button>
      )
    );
  }
  return (
    <form
      className={styles.customForm}
      onSubmit={(e) => {
        e.preventDefault();
        draft.submit();
      }}
    >
      <input
        className={styles.initInput}
        style={{ width: 90 }}
        type="text"
        value={draft.name}
        onChange={(e) => draft.setName(e.target.value)}
        placeholder="Name..."
        // eslint-disable-next-line jsx-a11y/no-autofocus -- the custom-name field appears on the user's own click
        autoFocus
        onBlur={() => {
          if (!draft.name.trim()) draft.setOpen(false);
        }}
      />
      <button type="submit" className={styles.iconBtn} disabled={!draft.name.trim()}>
        +
      </button>
    </form>
  );
}

type HeadProps = Pick<
  OrderRailProps,
  'trackerState' | 'locked' | 'onTogglePause' | 'onRerollAll' | 'onResetCombat' | 'showDialog'
> & {
  hasParticipants: boolean;
  onTrackEveryone: (() => void) | null;
  custom: CustomDraft;
};

/** The tracker's head: the round and the order's controls. */
export function TrackerHead(p: HeadProps) {
  const { trackerState, locked } = p;
  const idle = locked || !p.hasParticipants;
  return (
    <div className={styles.head}>
      <span className={styles.roundBadge} data-testid="turn-round">
        Round {trackerState.round}
      </span>
      {p.onTrackEveryone && !locked && (
        <button
          className={styles.iconBtn}
          data-testid="turn-track-all"
          onClick={p.onTrackEveryone}
          title="Track everyone not yet in the order"
          aria-label="Track everyone not yet in the order"
        >
          {'⇊'}
        </button>
      )}
      <button
        className={styles.iconBtn}
        data-testid="turn-reroll-all"
        onClick={p.onRerollAll}
        disabled={idle}
        title="Reroll initiative for everyone"
        aria-label="Reroll initiative for everyone"
      >
        {'↻'}
      </button>
      <ResetButton disabled={idle} locked={locked} showDialog={p.showDialog} onResetCombat={p.onResetCombat} />
      <button
        className={clsx(styles.iconBtn, trackerState.paused && styles.pauseActive)}
        data-testid="turn-pause"
        onClick={p.onTogglePause}
        disabled={locked}
        title={trackerState.paused ? 'Resume auto-advance' : 'Pause auto-advance'}
      >
        {trackerState.paused ? '⏸' : '▶'}
      </button>
      <AddCustom draft={p.custom} locked={locked} />
    </div>
  );
}

export function ManualOrderNote({
  locked,
  onSetOrderMode,
}: {
  locked: boolean;
  onSetOrderMode: OrderRailProps['onSetOrderMode'];
}) {
  return (
    <div className={styles.note} data-testid="turn-order-mode">
      {'✋ manual order'}
      <button
        type="button"
        className={styles.linkBtn}
        data-testid="turn-order-mode-auto"
        onClick={() => onSetOrderMode('initiative')}
        disabled={locked}
        title="Sort by initiative again"
      >
        {'⚡ back to initiative'}
      </button>
    </div>
  );
}

/**
 * THE JOIN HINT. Passive, never a modal; it clears itself on the next
 * round advance. "The others" is the point: the arrivals rolled seconds
 * ago on admission.
 */
export function JoinHint({
  joinedCount,
  onRerollOthers,
  onDismiss,
}: {
  joinedCount: number;
  onRerollOthers: () => void;
  onDismiss: () => void;
}) {
  return (
    <div className={clsx(styles.note, styles.joinHint)} data-testid="turn-join-hint">
      {joinedCount} joined
      <button
        type="button"
        className={styles.linkBtn}
        data-testid="turn-reroll-others"
        onClick={onRerollOthers}
        title="Reroll initiative for everyone who was already here"
      >
        {'↻ Reroll the others'}
      </button>
      <button
        type="button"
        className={styles.linkBtn}
        data-testid="turn-dismiss-join-hint"
        onClick={onDismiss}
        aria-label="Dismiss"
        title="Dismiss"
      >
        {'✕'}
      </button>
    </div>
  );
}

export function EndTurnFoot({
  locked,
  hasParticipants,
  endSuggested,
  onEndTurn,
}: {
  locked: boolean;
  hasParticipants: boolean;
  endSuggested: boolean;
  onEndTurn: () => void;
}) {
  return (
    <div className={styles.foot}>
      <button
        className={clsx(styles.endTurnBtn, endSuggested && styles.endSuggested)}
        data-testid="turn-end"
        data-suggested={endSuggested || undefined}
        onClick={onEndTurn}
        disabled={locked || !hasParticipants}
        title={hasParticipants ? 'End current turn (E)' : 'Nobody is in the order - track someone first'}
      >
        End Turn
      </button>
    </div>
  );
}

export function emptyMessage(active: boolean, hasParticipants: boolean, canTrackAll: boolean): string | null {
  if (!active || hasParticipants) return null;
  return canTrackAll
    ? 'No participants - ⇊ tracks everyone, or right-click a row'
    : 'No participants - right-click a row to track';
}

/** What the tracker's state says about the order: who is in it, and how it sorts. */
export function orderFacts(trackerState: TurnTrackerState, locked: boolean) {
  // A tracker document from before v8.73.0 has no `joined`, and a test
  // fixture may carry no participants at all; the rail renders either way.
  const participants = trackerState.participants ?? [];
  const joinedCount = (trackerState.joined ?? []).length;
  const manual = trackerState.orderMode === 'manual';
  const hasInitiative = participants.some((p) => p.initiative != null);
  return {
    participants,
    joinedCount,
    manual,
    hasParticipants: participants.length > 0,
    showJoinHint: joinedCount > 0 && joinedCount < participants.length,
    orderDraggable: trackerState.active && (manual || !hasInitiative) && !locked,
  };
}

export function RailFrame({ children }: { children: ReactNode }) {
  return (
    <div className={styles.rail} data-testid="order-strip">
      {children}
    </div>
  );
}
